<script setup lang="ts">
import {onClickOutside} from "@vueuse/core";
import {CheckIcon, ChevronDownIcon, XIcon} from "lucide-vue-next";
import {onMounted, type Ref, ref} from "vue";
import type {ConfigurationAdditionResponse} from "../../../responses/ConfigurationAdditionResponse.ts";
import type {ConfigurationCategoryResponse} from "../../../responses/ConfigurationCategoryResponse.ts";
import {__} from "../../../translate.ts";
import {Money} from "../../../utils/money.ts";
import {label} from "../../mixins/translations.ts";
import {useConfigurationStore} from "../../stores/configuration.ts";

interface Props {
    category: ConfigurationCategoryResponse
    addition: ConfigurationAdditionResponse
}

const props = defineProps<Props>();
const isOpen = ref(true);
const configurationStore = useConfigurationStore();
const showPopup = ref(false);

const target = ref(null);
onClickOutside(target, event => showPopup.value = false);
</script>

<template>
    <div class="border border-secondary-200 transition" v-if="isOpen" :class="configurationStore.hasAddition(addition.id) ? 'bg-secondary-100' : 'bg-white'">
        <div
            class="grid w-full grid-cols-[80px_1fr_auto] gap-0 text-left md:grid-cols-[104px_1fr_auto] hover:cursor-pointer"
            @click="configurationStore.toggleAddition(category, addition)"
            :class="configurationStore.hasAddition(addition.id) && addition.options.length > 0 ? 'items-start' : 'items-center'">

            <img :src="addition.image_url" :alt="addition.image_alt" class="h-full min-h-24 w-full object-cover">

            <div class="flex flex-column">
            <div class="min-w-0 w-full px-4 md:px-6" :class="configurationStore.hasAddition(addition.id) && addition.options.length > 0 ? 'py-5' : 'py-4'">
                <div class="flex items-center gap-3">
                    <span class="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition border-secondary-300 bg-white"
                          :class="configurationStore.hasAddition(addition.id) ? 'border-primary bg-white text-primary' : 'border-secondary-300 bg-white text-transparent'">
                        <CheckIcon class="h-3.5 w-3.5 stroke-[2.5]" />
                    </span>
                    <div class="min-w-0 flex flex-wrap items-center gap-2">
                        <span class="text-sm font-medium md:text-base" :class="configurationStore.hasAddition(addition.id) ? 'text-secondary-700' : 'text-secondary-500'">{{ addition.name }}</span>

                        <div v-if="addition.description" class="relative shrink-0" ref="target">
                            <button
                                type="button"
                                @click.stop="showPopup = !showPopup"
                                class="inline-flex h-5 w-5 items-center justify-center rounded-full border border-primary text-[10px] font-medium text-primary transition hover:bg-white"
                                aria-label="Meer informatie">
                                i
                            </button>

                            <div v-if="showPopup" class="absolute bottom-[calc(100%+14px)] left-1/2 z-20 hidden w-[320px] -translate-x-1/2 bg-white px-6 py-6 shadow-lg md:block lg:w-[420px]">
                                <div class="flex items-center justify-center">
                                    <img
                                        :src="addition.image_url"
                                        :alt="addition.image_alt"
                                        class="w-9/12 object-cover"
                                    />
                                </div>

                                <span class="mt-4 flex text-center text-sm leading-6 text-secondary-700" v-html="addition.description">
                                </span>

                                <span class="absolute left-1/2 top-full h-5 w-5 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-white"></span>

                                <button
                                    type="button"
                                    @click.stop="showPopup = false"
                                    class="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-secondary-100 text-secondary-400 transition hover:text-secondary-700"
                                    aria-label="Sluit info"
                                >
                                    <XIcon class="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="addition.description && showPopup" class="mt-4 bg-white text-secondary-700 md:hidden">
                    <div class="flex items-start justify-between gap-3 text-sm">
                        <p v-html="addition.description"></p>
                    </div>
                </div>

                <div class="mt-4 max-w-inherit md:max-w-[330px] pl-0 md:pl-9" v-if="configurationStore.hasAddition(addition.id) && addition.options.length > 0">
                    <div class="relative">
                        <select class="h-12 w-full appearance-none rounded-full border border-[#8dd0c3] bg-white px-5 pr-0 md:pr-12 text-sm text-secondary-600 outline-none"
                                @click.stop
                                @change="configurationStore.selectAddition(category, addition, $event.target.value, true)">
                            <option
                                v-for="option in addition.options"
                                :key="option.id"
                                :value="option.id"
                                :selected="configurationStore.selectedAdditions[addition.id]?.option?.id === option.id"
                            >
                                {{ option.label }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
            </div>
            <div class="px-4 text-right md:px-6 self-center py-4">
                <div class="text-sm font-medium md:text-base text-secondary-300" v-if="addition.price">
                    <span v-if="configurationStore.priceIncl">{{ Money.format(addition.price?.price_incl, addition.price?.currency) }}</span>
                    <span v-else>{{ Money.format(addition.price?.price_excl, addition.price?.currency) }}</span>
                </div>
                <span class="text-sm font-medium md:text-base text-secondary-300" v-else-if="addition.price_included">
                    {{ label('site.configuration.price_included') }}
                </span>
            </div>
        </div>
    </div>
</template>
